/**
 * DatePicker Component
 *
 * Calendar picker for date selection.
 * Ported from Vue DatePicker.vue
 *
 * Features:
 * - Week view: 7-column day grid with week numbers
 * - Month view: 3-column month grid
 * - Year view: 3-column month grid for 12-month window
 * - Navigation: Previous/Next, Go to Today
 * - Highlights selected period
 */

import React, { useMemo, useCallback, useState, useEffect } from "react";
import { formatAPIDate } from "@/utils/date";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addWeeks,
  addMonths,
  addYears,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  isWeekend,
  isSunday,
  getISOWeek,
  eachDayOfInterval,
  isBefore,
} from "date-fns";
import { nb } from "date-fns/locale";
import classNames from "classnames";
import { Icon } from "@/components/ui/display/Icon";
import { IconButton } from "@/components/ui/buttons/IconButton";
import type { DatePickerProps, DayGridItem } from "./types";
import styles from "./DatePicker.module.css";

// Norwegian red days (public holidays)
const RED_DAYS = [
  "2024-01-01",
  "2024-03-28",
  "2024-03-29",
  "2024-04-01",
  "2024-05-01",
  "2024-05-09",
  "2024-05-17",
  "2024-05-20",
  "2024-12-25",
  "2024-12-26",
  "2025-01-01",
  "2025-04-17",
  "2025-04-18",
  "2025-04-21",
  "2025-05-01",
  "2025-05-17",
  "2025-05-29",
  "2025-06-09",
  "2025-12-25",
  "2025-12-26",
  "2026-01-01",
  "2026-04-02",
  "2026-04-03",
  "2026-04-06",
  "2026-05-01",
  "2026-05-14",
  "2026-05-17",
  "2026-05-25",
  "2026-12-25",
  "2026-12-26",
];

const MONTH_NAMES = [
  "Januar",
  "Februar",
  "Mars",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Desember",
];

const isRedDay = (date: Date): boolean => {
  const dateStr = formatAPIDate(date);
  return isSunday(date) || RED_DAYS.includes(dateStr);
};

export const DatePicker: React.FC<DatePickerProps> = ({
  viewMode,
  startDate,
  endDate,
  onDateSelect,
  onPrevious,
  onNext,
  onToday,
  disabled = false,
  size = "md",
  className,
  "data-testid": testId,
}) => {
  // Local browsing state for calendar display (separate from committed startDate)
  // This allows browsing the calendar without changing the schedule view
  // (matches Vue DatePicker behavior)
  const [browsingDate, setBrowsingDate] = useState<Date>(startDate);

  // Sync browsingDate when startDate changes (external navigation via header or clicks)
  useEffect(() => {
    setBrowsingDate(startDate);
  }, [startDate]);

  // Use browsingDate for calendar display
  const focusDate = browsingDate;

  // ============ COMPUTED VALUES ============

  const isMonthGrid = viewMode === "month" || viewMode === "year";
  const isDayGrid = viewMode === "week";

  // Date title (e.g., "Uke 48" or "November 2025")
  const dateTitle = useMemo(() => {
    switch (viewMode) {
      case "week":
        return `Uke ${getISOWeek(startDate)}`;
      case "month": {
        const monthName = format(startDate, "MMMM", { locale: nb });
        const capitalizedMonth =
          monthName.charAt(0).toUpperCase() + monthName.slice(1);
        return capitalizedMonth;
      }
      case "year":
        return format(startDate, "yyyy");
      default:
        return format(startDate, "d. MMM yyyy", { locale: nb });
    }
  }, [viewMode, startDate]);

  // Footer title for month navigation
  const footerTitle = useMemo(() => {
    if (viewMode === "week") {
      const monthName = format(focusDate, "MMMM", { locale: nb });
      const capitalizedMonth =
        monthName.charAt(0).toUpperCase() + monthName.slice(1);
      return `${capitalizedMonth} ${format(focusDate, "yyyy")}`;
    }
    if (viewMode === "month") {
      return format(focusDate, "yyyy");
    }
    return "";
  }, [viewMode, focusDate]);

  // Generate day grid data for week view
  const monthGridData = useMemo((): DayGridItem[] => {
    // Get boundaries rounded by week
    const firstDate = startOfWeek(startOfMonth(focusDate), { weekStartsOn: 1 });
    let lastDate = endOfWeek(endOfMonth(focusDate), { weekStartsOn: 1 });

    // Pad to always show 6 weeks
    const calculateWeekCount = (start: Date, end: Date): number => {
      let count = 0;
      let current = start;
      while (isBefore(current, end) || isSameDay(current, end)) {
        count++;
        current = addWeeks(current, 1);
      }
      return count;
    };

    while (calculateWeekCount(firstDate, lastDate) < 6) {
      lastDate = addWeeks(lastDate, 1);
    }

    const days = eachDayOfInterval({ start: firstDate, end: lastDate });
    const today = new Date();

    return days.map((date) => ({
      date,
      red: isRedDay(date),
      weekend: isWeekend(date),
      today: isSameDay(date, today),
      isOtherMonth: !isSameMonth(date, focusDate),
      isHighlighted: isWithinInterval(date, { start: startDate, end: endDate }),
    }));
  }, [focusDate, startDate, endDate]);

  // Week numbers for the week column
  const displayWeeks = useMemo((): string[] => {
    if (viewMode !== "week") {
      return ["Q1", "Q2", "Q3", "Q4"];
    }

    const weeks: number[] = [];
    let date = monthGridData[0]?.date ?? startDate;
    const lastDate = monthGridData[monthGridData.length - 1]?.date ?? endDate;

    while (isBefore(date, lastDate) || isSameDay(date, lastDate)) {
      const weekNum = getISOWeek(date);
      if (!weeks.includes(weekNum)) {
        weeks.push(weekNum);
      }
      date = addWeeks(date, 1);
    }

    return weeks.map(String);
  }, [viewMode, monthGridData, startDate, endDate]);

  // ============ HANDLERS ============

  const handleDateClick = useCallback(
    (date: Date) => {
      if (disabled) return;
      onDateSelect(date);
    },
    [disabled, onDateSelect],
  );

  const handleMonthClick = useCallback(
    (monthIndex: number) => {
      if (disabled) return;
      const year = focusDate.getFullYear();
      const newDate = new Date(year, monthIndex, 1);
      onDateSelect(newDate);
    },
    [disabled, focusDate, viewMode, onDateSelect],
  );

  const showSelectToday = useMemo(() => {
    const today = new Date();

    if (viewMode === "year") {
      return !(
        isSameMonth(startDate, today) &&
        startDate.getFullYear() === today.getFullYear()
      );
    }

    return !isWithinInterval(today, { start: startDate, end: endDate });
  }, [endDate, startDate, viewMode]);

  const handleTodayClick = useCallback(() => {
    if (disabled || !showSelectToday) return;
    onToday();
  }, [disabled, onToday, showSelectToday]);

  const handlePrevClick = useCallback(() => {
    if (disabled) return;
    onPrevious();
  }, [disabled, onPrevious]);

  const handleNextClick = useCallback(() => {
    if (disabled) return;
    onNext();
  }, [disabled, onNext]);

  // Footer navigation - changes browsing state only (like Vue)
  // Week view: browse by months (parent period)
  // Month view: browse by years (parent period)
  const handleFooterPrev = useCallback(() => {
    if (disabled) return;
    if (viewMode === "week") {
      setBrowsingDate((prev) => addMonths(prev, -1));
    } else if (viewMode === "month") {
      setBrowsingDate((prev) => addYears(prev, -1));
    }
  }, [disabled, viewMode]);

  const handleFooterNext = useCallback(() => {
    if (disabled) return;
    if (viewMode === "week") {
      setBrowsingDate((prev) => addMonths(prev, 1));
    } else if (viewMode === "month") {
      setBrowsingDate((prev) => addYears(prev, 1));
    }
  }, [disabled, viewMode]);

  // ============ RENDER ============

  return (
    <div
      className={classNames(
        styles.container,
        disabled && styles.disabled,
        className,
      )}
      data-size={size}
      data-view={viewMode}
      data-testid={testId}
    >
      {/* Header */}
      <div className={styles.header}>
        <button
          type="button"
          className={styles.selectToday}
          onClick={handleTodayClick}
          disabled={disabled || !showSelectToday}
          data-testid="date-picker-today-btn"
        >
          Til i dag
        </button>
        <div className={styles.headerTitle}>
          <IconButton
            icon={<Icon name="chevronLeft" size="lg" aria-hidden />}
            variant="navDark"
            onClick={handlePrevClick}
            disabled={disabled}
            aria-label="Forrige periode"
            data-testid="date-picker-prev-btn"
            className={styles.navButton}
          />
          <div className={styles.name} data-testid="date-picker-title">
            {dateTitle}
          </div>
          <IconButton
            icon={<Icon name="chevronRight" size="lg" aria-hidden />}
            variant="navDark"
            onClick={handleNextClick}
            disabled={disabled}
            aria-label="Neste periode"
            data-testid="date-picker-next-btn"
            className={styles.navButton}
          />
        </div>
      </div>

      {/* Grid Container */}
      <div className={styles.gridContainer}>
        {/* Week/Quarter Column */}
        <div className={styles.weekColumn}>
          {displayWeeks.map((week, idx) => (
            <div key={idx} className={styles.week}>
              {week}
            </div>
          ))}
        </div>

        {/* Month Grid (for month/year view) */}
        {isMonthGrid && (
          <div
            className={classNames(styles.calGrid, styles.monthGrid)}
            data-testid="date-picker-month-grid"
          >
            {Array.from({ length: 12 }, (_, m) => {
              // For both month and year view, only highlight the selected month
              const isHighlighted = startDate.getMonth() === m;
              const isToday =
                new Date().getMonth() === m &&
                new Date().getFullYear() === focusDate.getFullYear();

              return (
                <div
                  key={m}
                  className={classNames(
                    styles.cell,
                    styles.month,
                    isHighlighted && styles.highlight,
                    isToday && styles.today,
                  )}
                  onClick={() => handleMonthClick(m)}
                  data-testid={`date-picker-month-${m}`}
                >
                  {MONTH_NAMES[m]}
                </div>
              );
            })}
          </div>
        )}

        {/* Day Grid (for week view) */}
        {isDayGrid && (
          <div className={styles.calGrid} data-testid="date-picker-day-grid">
            {monthGridData.map((item) => (
              <div
                key={item.date.toISOString()}
                className={classNames(
                  styles.cell,
                  styles.day,
                  item.isOtherMonth && styles.otherMonth,
                  item.isHighlighted && styles.highlight,
                  item.today && styles.today,
                  item.red && styles.red,
                  item.weekend && styles.weekend,
                )}
                onClick={() => handleDateClick(item.date)}
                data-testid={`date-picker-day-${formatAPIDate(item.date)}`}
              >
                {item.date.getDate()}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer (for week/month view - allows broader navigation) */}
      {footerTitle && (
        <div className={styles.footer} data-testid="date-picker-footer">
          <div className={styles.footerTitle}>
            <IconButton
              icon={<Icon name="chevronLeft" size="md" aria-hidden />}
              variant="navDark"
              size="sm"
              onClick={handleFooterPrev}
              disabled={disabled}
              aria-label={viewMode === "month" ? "Forrige år" : "Forrige måned"}
              data-testid="date-picker-footer-prev-btn"
              className={styles.navButton}
            />
            <div className={styles.name} data-testid="date-picker-footer-title">
              {footerTitle}
            </div>
            <IconButton
              icon={<Icon name="chevronRight" size="md" aria-hidden />}
              variant="navDark"
              size="sm"
              onClick={handleFooterNext}
              disabled={disabled}
              aria-label={viewMode === "month" ? "Neste år" : "Neste måned"}
              data-testid="date-picker-footer-next-btn"
              className={styles.navButton}
            />
          </div>
        </div>
      )}
    </div>
  );
};

DatePicker.displayName = "DatePicker";

export default DatePicker;
